import { api } from './api';

// ---------------------------------------------------------------------------
// Construction plan API client (blueprint v2 B-3).
// Typed wrappers around /api/construction-plans. Plan content is the planData
// JSON payload; full PlanDataV2 typing lands with the §2 contract (B-4).
// ---------------------------------------------------------------------------

export interface PlanProjectRef {
  id: number;
  name: string;
  siteAddress?: string;
  status?: string;
}

export interface PlanUserRef {
  id?: number;
  firstName: string;
  lastName: string;
}

export interface ConstructionPlanRecord {
  id: number;
  projectId: number;
  project?: PlanProjectRef;
  createdById?: number;
  createdBy?: PlanUserRef;
  version: number;
  title: string;
  description: string | null;
  planData: Record<string, unknown>;
  classification: string | null;
  planType: string;
  signedById: number | null;
  signedAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  history?: ConstructionPlanRecord[];
  /** S5: project documents auto-linked to this plan (BOM/CD/RFDS/SCOPE). */
  linkedDocuments?: LinkedDocument[];
  /** DE-006: JHAs linked to this plan via the constructionPlanId back-link. */
  linkedJhas?: Array<{ id: number; title?: string | null; signedAt?: string | null }>;
}

export interface LinkedDocument {
  id: number;
  originalName: string;
  docType: string;
  mimeType: string;
}

export interface CreatePlanPayload {
  projectId: number;
  title: string;
  description?: string | null;
  planType?: string;
  planData: Record<string, unknown>;
}

export interface UpdatePlanPayload {
  title?: string;
  description?: string | null;
  planData?: Record<string, unknown>;
  editSummary?: string;
}

export interface ListPlansParams {
  projectId?: number;
  classification?: 'II' | 'III' | 'IV';
  isActive?: boolean;
  allVersions?: boolean;
}

export type PlanAttachmentType =
  | 'craneLiftPlan'
  | 'structuralAnalysis'
  | 'mountAnalysis'
  | 'qeReport'
  | 'proof-test'
  | 'other';

/** Human-readable error message for a failed plan API call (toast copy). */
export function planApiErrorMessage(error: unknown, fallback: string): string {
  const axiosError = error as {
    response?: { data?: { message?: string; errors?: { field: string; message: string }[] } };
    message?: string;
  };
  const data = axiosError?.response?.data;
  if (data?.message) {
    if (data.errors?.length) {
      return `${data.message}: ${data.errors[0].field} ${data.errors[0].message}`;
    }
    return data.message;
  }
  if (axiosError?.message === 'Network Error') {
    return 'Cannot reach the server — check your connection and try again.';
  }
  return fallback;
}

export async function createPlan(payload: CreatePlanPayload): Promise<ConstructionPlanRecord> {
  const response = await api.post<{ success: boolean; data: ConstructionPlanRecord }>(
    '/construction-plans',
    payload
  );
  return response.data.data;
}

export async function updatePlan(id: number, payload: UpdatePlanPayload): Promise<ConstructionPlanRecord> {
  const response = await api.put<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}`,
    payload
  );
  return response.data.data;
}

export async function getPlan(id: number): Promise<ConstructionPlanRecord> {
  const response = await api.get<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}`
  );
  return response.data.data;
}

export async function listPlans(params: ListPlansParams = {}): Promise<ConstructionPlanRecord[]> {
  const query: Record<string, string | number> = {};
  if (params.projectId != null) query.projectId = params.projectId;
  if (params.classification) query.classification = params.classification;
  if (params.isActive != null) query.isActive = String(params.isActive);
  if (params.allVersions != null) query.allVersions = String(params.allVersions);
  const response = await api.get<{ success: boolean; data: ConstructionPlanRecord[] }>(
    '/construction-plans',
    { params: query }
  );
  return response.data.data;
}

export async function signOff(id: number): Promise<ConstructionPlanRecord> {
  const response = await api.post<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}/sign-off`
  );
  return response.data.data;
}

export async function upgradeToV2(id: number): Promise<ConstructionPlanRecord> {
  const response = await api.post<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}/upgrade-v2`
  );
  return response.data.data;
}

export async function uploadAttachment(
  id: number,
  file: File,
  type: PlanAttachmentType = 'other',
  metadata?: Record<string, unknown>
): Promise<ConstructionPlanRecord> {
  const form = new FormData();
  form.append('file', file);
  form.append('type', type);
  if (metadata) form.append('metadata', JSON.stringify(metadata));
  const response = await api.post<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.data;
}

/** F-5: remove a supporting document via the existing B-2 DELETE route. */
export async function deleteAttachment(
  id: number,
  name: string
): Promise<ConstructionPlanRecord> {
  const response = await api.delete<{ success: boolean; data: ConstructionPlanRecord }>(
    `/construction-plans/${id}/attachments/${encodeURIComponent(name)}`
  );
  return response.data.data;
}
